import React, { ReactNode, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { logout } from '../../store/authSlice';

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="bg-black text-white p-2 md:p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          {/* Version mobile : Menu burger - LOGO - Bt + */}
          <div className="md:hidden">
            <button 
              onClick={toggleMenu}
              className="text-white focus:outline-none"
            >
              <svg 
                className="w-10 h-10" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 6h16M4 12h16M4 18h16" 
                />
              </svg>
            </button>
          </div>

          {/* Logo centré en version mobile */}
          <div className="md:hidden flex justify-center">
            <Link to="/">
              <img 
                src="/favicon.ico" 
                alt="LiveMatch Logo" 
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Bouton + en version mobile */}
          <div className="md:hidden">
            <Link 
              to="/config-match" 
              className="text-white focus:outline-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </Link>
          </div>

          {/* Version desktop */}
          <div className="hidden md:flex items-center justify-between w-full">
            <div className="w-1/4"></div>
            
            {/* Éléments centraux */}
            <div className="flex items-center justify-center space-x-8 w-2/4">
              <Link 
                to="/matchs" 
                className="text-2xl font-semibold hover:text-blue-400 transition-colors duration-300 transform hover:scale-105"
              >
                Matchs
              </Link>
              
              <Link to="/" className="mx-4">
                <img 
                  src="/favicon.ico" 
                  alt="LiveMatch Logo" 
                  className="h-12 w-auto transition-transform duration-300 hover:scale-110"
                />
              </Link>
              
              <Link 
                to="/config-match" 
                className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg transition-all duration-300 text-xl transform hover:scale-105 shadow-lg hover:shadow-2xl"
              >
                Nouveau Match
              </Link>
            </div>
            
            <div className="flex items-center space-x-4 justify-end w-1/4">
              
              {/* Bouton de profil */}
              {isAuthenticated && (
                <button
                  onClick={() => navigate('/profil')}
                  className="bg-blue-500 text-white hover:bg-blue-600 p-2 rounded-full flex items-center justify-center ml-4 transition-colors duration-300 w-10 h-10"
                  title="Mon Profil"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
              )}
              
              {/* Bouton de connexion/déconnexion */}
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    dispatch(logout());
                    navigate('/');
                  }}
                  className="bg-white text-black hover:bg-gray-100 p-2 rounded-full flex items-center justify-center ml-4 w-10 h-10"
                  title="Déconnexion"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="bg-white text-black hover:bg-gray-100 p-2 rounded-full flex items-center justify-center ml-4 w-10 h-10"
                  title="Connexion"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Menu mobile */}
        {isMenuOpen && (
          <div className="md:hidden fixed left-0 right-0 bg-black z-50">
            <div className="px-4 pt-2 pb-4 space-y-4">
              <Link 
                to="/matchs" 
                className="block hover:text-blue-400 py-3 text-xl transition-colors duration-300"
                onClick={toggleMenu}
              >
                Matchs
              </Link>
              <Link 
                to="/statistiques" 
                className="block hover:text-blue-400 py-3 text-xl transition-colors duration-300"
                onClick={toggleMenu}
              >
                Statistiques
              </Link>
              
              {/* Lien vers le profil pour mobile */}
              {isAuthenticated && (
                <Link 
                  to="/profil" 
                  className="block hover:text-blue-400 py-3 text-xl transition-colors duration-300"
                  onClick={toggleMenu}
                >
                  Mon Profil
                </Link>
              )}
              
              {/* Bouton de connexion/déconnexion pour mobile */}
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    dispatch(logout());
                    navigate('/');
                    toggleMenu();
                  }}
                  className="block w-full text-left hover:text-blue-400 py-3 text-xl transition-colors duration-300"
                >
                  Déconnexion
                </button>
              ) : (
                <button
                  onClick={() => {
                    navigate('/login');
                    toggleMenu();
                  }}
                  className="block w-full text-left hover:text-blue-400 py-3 text-xl transition-colors duration-300"
                >
                  Connexion
                </button>
              )}
            </div>
          </div>
        )}
      </nav>
      
      <main className="container mx-auto p-2 md:p-4 bg-white">
        {children}
      </main>
      
      <footer className="bg-gray-800 text-white p-2 md:p-4 text-center text-sm">
        <p>&copy; 2025 LiveMatch. Tous droits réservés.</p>
      </footer>
    </div>
  );
};

export default MainLayout;
